import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

export type StatusKind = 'aktiv' | 'laufend' | 'inaktiv' | 'pending' | 'fehler' | 'abgelaufen';

export function customerStatusKind(status: string): StatusKind {
  if (status === 'ACTIVE') return 'aktiv';
  if (status === 'INACTIVE') return 'inaktiv';
  return 'pending';
}

const I18N_KEYS: Record<StatusKind, string> = {
  aktiv: 'statusPill.aktiv',
  laufend: 'statusPill.laufend',
  inaktiv: 'statusPill.inaktiv',
  pending: 'statusPill.pending',
  fehler: 'statusPill.fehler',
  abgelaufen: 'statusPill.abgelaufen',
};

@Component({
  selector: 'app-status-pill',
  standalone: true,
  imports: [TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span class="status-pill" [class]="'status-pill--' + kind() + (size() === 'sm' ? ' status-pill--sm' : '')">
      <span class="status-pill__dot" aria-hidden="true"></span>
      {{ label() ?? (labelKey() | translate) }}
    </span>
  `,
  styles: `
    :host {
      display: inline-flex;
    }

    .status-pill {
      display: inline-flex;
      align-items: center;
      gap: 0.4375rem;
      padding: 0.3125rem 0.6875rem;
      border-radius: 999px;
      font-size: 0.75rem;
      font-weight: 600;
      white-space: nowrap;

      &--sm {
        padding: 0.1875rem 0.5625rem;
        font-size: 0.6875rem;
      }

      &--aktiv {
        background: var(--color-status-aktiv-bg);
        color: var(--color-status-aktiv-fg);
      }
      &--laufend {
        background: var(--color-status-laufend-bg);
        color: var(--color-status-laufend-fg);
      }
      &--inaktiv {
        background: var(--color-status-inaktiv-bg);
        color: var(--color-status-inaktiv-fg);
      }
      &--pending {
        background: var(--color-status-pending-bg);
        color: var(--color-status-pending-fg);
      }
      &--fehler {
        background: var(--color-status-fehler-bg);
        color: var(--color-status-fehler-fg);
      }
      &--abgelaufen {
        background: var(--color-status-abgelaufen-bg);
        color: var(--color-status-abgelaufen-fg);
      }
    }

    .status-pill__dot {
      width: 0.375rem;
      height: 0.375rem;
      border-radius: 50%;
      flex-shrink: 0;

      .status-pill--aktiv & {
        background: var(--color-status-aktiv-dot);
      }
      .status-pill--laufend & {
        background: var(--color-status-laufend-dot);
      }
      .status-pill--inaktiv & {
        background: var(--color-status-inaktiv-dot);
      }
      .status-pill--pending & {
        background: var(--color-status-pending-dot);
      }
      .status-pill--fehler & {
        background: var(--color-status-fehler-dot);
      }
      .status-pill--abgelaufen & {
        background: var(--color-status-abgelaufen-dot);
      }
    }
  `,
})
export class StatusPillComponent {
  readonly kind = input<StatusKind>('aktiv');
  readonly label = input<string | null>(null);
  readonly size = input<'sm' | 'md'>('md');

  readonly labelKey = computed(() => I18N_KEYS[this.kind()] ?? this.kind());
}
